import React, { useState, useContext, useEffect, useRef, useCallback, useMemo } from "react";
import _ from "lodash";

import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import DecklistGrid from "../components/downloader/DecklistGrid";
import HeadToHeadGrid from "../components/downloader/HeadtoHeadGrid";
import PlayerProfileGrid from "../components/downloader/PlayerProfileGrid";
import StoryboardsGrid from "../components/downloader/StoryboardsGrid";
import TeamsGrid from "../components/downloader/TeamsGrid";
import TeamAggregateGrid from "../components/downloader/TeamAggregateGrid";
import AllPlayersGrid from "../components/downloader/AllPlayersGrid";

import DownloaderWidget from "../components/widgets/Downloader";
import DownloaderPreview from "../components/downloader/DownloaderPreview";

import Wrapper from "../components/layout/Wrapper";

import bkgndBlack from "../assets/bkgnd-black.png";
import bkgndBlue from "../assets/bkgnd-blue.png";
import bkgndGreen from "../assets/bkgnd-green.png";
import bkgndRed from "../assets/bkgnd-red.png";
import bkgndSmoke from "../assets/bkgnd-smoke.png";
import bkgndWhite from "../assets/bkgnd-white.png";

// Memoized TabPanel component to prevent recreation on every render
const TabPanel = React.memo(function TabPanel(props) {
   const { children, value, index, ...other } = props;

   return (
      <div
         role="tabpanel"
         hidden={value !== index}
         id={`simple-tabpanel-${index}`}
         aria-labelledby={`simple-tab-${index}`}
         {...other}
      >
         {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
      </div>
   );
});

// Memoized child components to prevent unnecessary re-renders
const MemoizedDecklistGrid = React.memo(DecklistGrid);
const MemoizedHeadToHeadGrid = React.memo(HeadToHeadGrid);
const MemoizedPlayerProfileGrid = React.memo(PlayerProfileGrid);
const MemoizedStoryboardsGrid = React.memo(StoryboardsGrid);
const MemoizedTeamsGrid = React.memo(TeamsGrid);
const MemoizedTeamAggregateGrid = React.memo(TeamAggregateGrid);
const MemoizedAllPlayersGrid = React.memo(AllPlayersGrid);

export default function Downloader({ parent }) {
   const columnWidth = useRef();
   const [activeTab, setActiveTab] = useState(0);
   const [activePreview, setActivePreview] = useState();
   const [downloadName, setDownloadName] = useState("");
   const [currentPage, setCurrentPage] = useState(0);
   const [maxPage, setMaxPage] = useState(0);
   const [previewActiveData, setPreviewActiveData] = useState(null);
   const [sideboardVisible, setSideboardVisible] = useState(false);
   const [decklistMetadata, setDecklistMetadata] = useState(null);
   const storyboardBaseNameRef = useRef(null);
   const teamBaseNameRef = useRef(null);
   const teamAggregateBaseNameRef = useRef(null);
   const allPlayersBaseNameRef = useRef(null);

   const [background, setBkgnd] = useState("black");

   const backgrounds = {
      black: bkgndBlack,
      blue: bkgndBlue,
      green: bkgndGreen,
      red: bkgndRed,
      smoke: bkgndSmoke,
      white: bkgndWhite,
   };

   // WINDOW LISTENER
   const iframeScale =
      columnWidth.current &&
      (parseInt(window.getComputedStyle(columnWidth.current).width) -
         parseInt(
            window
               .getComputedStyle(columnWidth.current)
               .getPropertyValue("padding-left")
         )) /
      1920;

   const [windowSize, setWindowSize] = useState({});

   // Helper function to update decklist download name with suffixes
   const updateDecklistDownloadName = useCallback((baseName, archetype, isDraftParam, showSideboard) => {
      if (!baseName.startsWith("Decklist_")) {
         return baseName;
      }
      
      // Remove existing suffixes if present
      let name = baseName.replace(/_Draft/g, "").replace(/_Sideboard/g, "");
      
      // Check if Archetype contains "Draft" (case-insensitive)
      const hasDraftInArchetype = archetype && archetype.toLowerCase().includes("draft");
      
      // Add _Draft if Archetype contains "Draft" or if isDraft param is true
      if (hasDraftInArchetype || isDraftParam) {
         name += "_Draft";
      }
      
      // Add _Sideboard if sideboard is visible
      if (showSideboard) {
         name += "_Sideboard";
      }
      
      return name;
   }, []);

   const loadDownloader = useCallback((type, id, param = "", name = "Download") => {
      setActivePreview({ type: type, id: id, param: param });
      
      // Store base name for storyboards, teams, team aggregate, and allplayers (before Round suffix is added)
      if (type === "storyboard") {
         storyboardBaseNameRef.current = name;
         teamBaseNameRef.current = null;
         teamAggregateBaseNameRef.current = null;
         allPlayersBaseNameRef.current = null;
      } else if (type === "team") {
         teamBaseNameRef.current = name;
         storyboardBaseNameRef.current = null;
         teamAggregateBaseNameRef.current = null;
         allPlayersBaseNameRef.current = null;
      } else if (type === "teamaggregate") {
         // For team aggregate, remove existing Round suffix if present
         const roundMatch = name.match(/^(.+?)(_Round[^_]*)?$/);
         teamAggregateBaseNameRef.current = roundMatch && roundMatch[1] ? roundMatch[1] : name;
         storyboardBaseNameRef.current = null;
         teamBaseNameRef.current = null;
         allPlayersBaseNameRef.current = null;
      } else if (type === "allplayers") {
         // For allplayers, remove existing Round suffix if present
         const roundMatch = name.match(/^(.+?)(_Round[^_]*)?$/);
         allPlayersBaseNameRef.current = roundMatch && roundMatch[1] ? roundMatch[1] : name;
         storyboardBaseNameRef.current = null;
         teamBaseNameRef.current = null;
         teamAggregateBaseNameRef.current = null;
      } else {
         storyboardBaseNameRef.current = null;
         teamBaseNameRef.current = null;
         teamAggregateBaseNameRef.current = null;
         allPlayersBaseNameRef.current = null;
      }
      
      // Store decklist metadata if it's a decklist
      let archetype = "";
      if (type === "decklist") {
         // Extract metadata from the name string (format: "Decklist_LastNameFirstName_Archetype")
         // Remove any existing suffixes first
         let cleanName = name.replace(/_Draft/g, "").replace(/_Sideboard/g, "");
         const nameParts = cleanName.replace("Decklist_", "").split("_");
         
         if (nameParts.length >= 2) {
            archetype = nameParts[nameParts.length - 1];
            const nameWithoutArchetype = nameParts.slice(0, -1).join("_");
            setDecklistMetadata({
               baseName: nameWithoutArchetype,
               archetype: archetype,
               originalName: cleanName
            });
         } else {
            setDecklistMetadata({
               baseName: cleanName.replace("Decklist_", ""),
               archetype: "",
               originalName: cleanName
            });
         }
      } else {
         setDecklistMetadata(null);
      }
      
      // Update download name with proper suffixes
      const isDraft = type === "decklist" && param === "draft";
      const updatedName = updateDecklistDownloadName(name, archetype, isDraft, false);
      setDownloadName(updatedName);
      setCurrentPage(0); // Reset to first page when loading new preview
      setSideboardVisible(false); // Reset sideboard visibility when loading new preview
   }, [updateDecklistDownloadName]);

   const handleToggleSideboard = useCallback(() => {
      setSideboardVisible((prev) => {
         const newValue = !prev;
         
         // Update download name when toggling sideboard
         if (activePreview && activePreview.type === "decklist" && downloadName && decklistMetadata) {
            const isDraft = activePreview.param === "draft";
            // Remove existing _Sideboard suffix, then rebuild with correct suffixes
            const baseName = downloadName.replace(/_Sideboard/g, "");
            const updatedName = updateDecklistDownloadName(baseName, decklistMetadata.archetype, isDraft, newValue);
            setDownloadName(updatedName);
         }
         
         return newValue;
      });
   }, [activePreview, downloadName, decklistMetadata, updateDecklistDownloadName]);

   // Callback to update preview data from child components
   const handlePreviewDataUpdate = useCallback((activeData) => {
      setPreviewActiveData(activeData);
   }, []);

   // Calculate max page when preview data changes
   useEffect(() => {
      if (previewActiveData && previewActiveData.rows && previewActiveData.rows.length > 0) {
         const totalRows = previewActiveData.rows.length;
         const rowsPerPage = (previewActiveData.Rows || 8) * (previewActiveData.Columns || 1);
         const pagesFrac = totalRows / rowsPerPage;
         
         if (pagesFrac <= 1) {
            setMaxPage(0);
         } else {
            setMaxPage(Math.floor(pagesFrac));
         }
      } else {
         setMaxPage(0);
      }
   }, [previewActiveData]);

   // Helper function to clean and format round name for filename
   const formatRoundNameForFilename = useCallback((roundName) => {
      if (!roundName) return "";
      // Clean round name for filename (remove special characters)
      let cleanRoundName = roundName.replace(/[^a-zA-Z0-9]/g, "");
      
      // Remove "Round" prefix if it exists (to avoid "RoundRound#")
      if (cleanRoundName.toLowerCase().startsWith("round")) {
         cleanRoundName = cleanRoundName.substring(5); // Remove "Round" (5 characters)
      }
      
      return cleanRoundName;
   }, []);

   // Helper function to calculate row range for filename
   const calculateRowRange = useCallback((currentPage, rows, columns) => {
      if (!rows || !columns) return "";
      const rowsPerPage = rows * columns;
      const startRow = currentPage * rowsPerPage + 1; // 1-indexed
      const endRow = (currentPage + 1) * rowsPerPage; // 1-indexed
      return `${startRow}-${endRow}`;
   }, []);

   // Update download name when Round becomes available or page changes for storyboards, teams, team aggregate, and allplayers
   useEffect(() => {
      const shouldUpdateName = activePreview && 
         (activePreview.type === "storyboard" || 
          activePreview.type === "team" || 
          activePreview.type === "teamaggregate" || 
          activePreview.type === "allplayers");
      
      if (shouldUpdateName && previewActiveData) {
         let baseName = null;
         
         // Get base name from appropriate ref
         if (activePreview.type === "storyboard" && storyboardBaseNameRef.current) {
            baseName = storyboardBaseNameRef.current;
         } else if (activePreview.type === "team" && teamBaseNameRef.current) {
            baseName = teamBaseNameRef.current;
         } else if (activePreview.type === "teamaggregate" && teamAggregateBaseNameRef.current) {
            baseName = teamAggregateBaseNameRef.current;
         } else if (activePreview.type === "allplayers" && allPlayersBaseNameRef.current) {
            baseName = allPlayersBaseNameRef.current;
         }
         
         if (baseName) {
            let updatedName = baseName;
            
            // Add Round name if available
            if (previewActiveData.Round) {
               const cleanRoundName = formatRoundNameForFilename(previewActiveData.Round);
               if (cleanRoundName) {
                  updatedName = `${updatedName}_Round${cleanRoundName}`;
               }
            }
            
            // Add row range
            const rowRange = calculateRowRange(
               currentPage,
               previewActiveData.Rows,
               previewActiveData.Columns
            );
            if (rowRange) {
               updatedName = `${updatedName}_${rowRange}`;
            }
            
            setDownloadName(updatedName);
         }
      }
   }, [previewActiveData?.Round, previewActiveData?.Rows, previewActiveData?.Columns, activePreview?.type, currentPage, formatRoundNameForFilename, calculateRowRange]);

   // Reset page when preview changes
   useEffect(() => {
      setCurrentPage(0);
      setPreviewActiveData(null);
   }, [activePreview]);

   const handlePrevPage = useCallback(() => {
      setCurrentPage((prev) => Math.max(0, prev - 1));
   }, []);

   const handleNextPage = useCallback(() => {
      setCurrentPage((prev) => Math.min(maxPage, prev + 1));
   }, [maxPage]);

   const clearDownloader = useCallback(() => {
      setActivePreview(null);
      setDownloadName("");
   }, []);

   const handleTabChange = useCallback((event, newValue) => {
      setActiveTab(newValue);
   }, []);

   useEffect(() => {
      function handleResize() {
         setWindowSize({
            height: window.innerHeight,
            width: window.innerWidth,
         });
      }

      window.addEventListener("resize", handleResize);

      return (_) => {
         window.removeEventListener("resize", handleResize);
      };
   });

   useEffect(() => {
      setWindowSize({
         height: window.innerHeight,
         width: window.innerWidth,
      });
   }, []);

   const a11yProps = useCallback((name, index) => {
      return {
         id: `${name}-tab-${index}`,
         "aria-controls": `${name}-tabpanel-${index}`,
      };
   }, []);

   return (
      <Wrapper>

<Typography
                  variant='h4'
                  component='div'
                  sx={{ textTransform: "uppercase", mb: 2 }}
               >
                  Graphics Downloader
               </Typography>

         <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Decklists" {...a11yProps("decklists", 0)} />
            <Tab label="Head To Head" {...a11yProps("headtohead", 1)} />
            <Tab label="Player Profile" {...a11yProps("playerprofile", 2)} />
            <Tab label="Storyboards" {...a11yProps("storyboards", 3)} />
            <Tab label="Teams" {...a11yProps("teams", 4)} />
            <Tab label="Team Aggregate" {...a11yProps("teamaggregate", 5)} />
            <Tab label="Player Standings" {...a11yProps("allplayers", 6)} />
         </Tabs>

         <TabPanel value={activeTab} index={0}>
            <MemoizedDecklistGrid
               loadDownloader={loadDownloader}
               clearDownloader={clearDownloader}
            />

         </TabPanel>

         <TabPanel value={activeTab} index={1}>
            <MemoizedHeadToHeadGrid
               loadDownloader={loadDownloader}
               clearDownloader={clearDownloader}
            />
         </TabPanel>

         <TabPanel value={activeTab} index={2}>
            <MemoizedPlayerProfileGrid
               loadDownloader={loadDownloader}
               clearDownloader={clearDownloader}
            />
         </TabPanel>

         <TabPanel value={activeTab} index={3}>
            <MemoizedStoryboardsGrid
               loadDownloader={loadDownloader}
               clearDownloader={clearDownloader}
            />
         </TabPanel>

         <TabPanel value={activeTab} index={4}>
            <MemoizedTeamsGrid
               loadDownloader={loadDownloader}
               clearDownloader={clearDownloader}
            />
         </TabPanel>

         <TabPanel value={activeTab} index={5}>
            <MemoizedTeamAggregateGrid
               loadDownloader={loadDownloader}
               clearDownloader={clearDownloader}
            />
         </TabPanel>

         <TabPanel value={activeTab} index={6}>
            <MemoizedAllPlayersGrid
               loadDownloader={loadDownloader}
               clearDownloader={clearDownloader}
            />
         </TabPanel>

         <Grid
            item
            xs={4}
            ref={columnWidth}
            sx={{ color: "#fff", position: "relative" }}
         >
            <Stack alignItems='flex-start' sx={{ mt: 4, mb: 2 }}>
               <Typography
                  variant='h3'
                  component='div'
                  sx={{ textTransform: "uppercase", whiteSpace: "nowrap" }}
               >
                  GRAPHIC BUILDER
               </Typography>


               <Stack
                  direction='row'
                  spacing={1}
                  width='100%'
                  justifyContent='space-between'
                  alignItems='stretch'
               >
                  <Stack
                     direction='row'
                     spacing={1}
                     justifyContent='center'
                  >
                     <Button
                        onClick={() => setBkgnd("black")}
                        variant={background == "black" ? "contained" : "outlined"}
                        color='secondary'
                     >
                        BLACK
                     </Button>
                     <Button
                        onClick={() => setBkgnd("blue")}
                        variant={background == "blue" ? "contained" : "outlined"}
                        color='secondary'
                     >
                        BLUE
                     </Button>
                     <Button
                        onClick={() => setBkgnd("green")}
                        variant={background == "green" ? "contained" : "outlined"}
                        color='secondary'
                     >
                        GREEN
                     </Button>
                     <Button
                        onClick={() => setBkgnd("red")}
                        variant={background == "red" ? "contained" : "outlined"}
                        color='secondary'
                     >
                        RED
                     </Button>
                     <Button
                        onClick={() => setBkgnd("smoke")}
                        variant={background == "smoke" ? "contained" : "outlined"}
                        color='secondary'
                     >
                        SMOKE
                     </Button>
                     <Button
                        onClick={() => setBkgnd("white")}
                        variant={background == "white" ? "contained" : "outlined"}
                        color='secondary'
                     >
                        WHITE
                     </Button>
                     <Button
                        onClick={() => setBkgnd(null)}
                        variant={background == null ? "contained" : "outlined"}
                        color='secondary'
                     >
                        TRANSPARENT
                     </Button>
                  </Stack>

                  <Stack direction='row' spacing={1} alignItems='center'>
                     {(activePreview && 
                        (activePreview.type === "storyboard" || 
                         activePreview.type === "team" || 
                         activePreview.type === "teamaggregate" || 
                         activePreview.type === "allplayers")) && (
                        <>
                           <Button
                              onClick={handlePrevPage}
                              variant="outlined"
                              color='secondary'
                              disabled={currentPage === 0}
                              size='small'
                           >
                              PREV
                           </Button>
                           <Button
                              onClick={handleNextPage}
                              variant="outlined"
                              color='secondary'
                              disabled={currentPage >= maxPage}
                              size='small'
                           >
                              NEXT
                           </Button>
                        </>
                     )}
                     {(activePreview && 
                        (activePreview.type === "decklist")) && (
                        <Button
                           onClick={handleToggleSideboard}
                           variant={sideboardVisible ? "contained" : "outlined"}
                           color='secondary'
                           size='small'
                        >
                           SIDEBOARD
                        </Button>
                     )}
                     <DownloaderWidget enabled={true} downloadName={downloadName}>
                        <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 100 }}>
                           <DownloaderPreview
                              activePreview={activePreview}
                              currentPage={currentPage}
                              onPreviewDataUpdate={handlePreviewDataUpdate}
                              sideboardVisible={sideboardVisible}
                           />
                        </Box>

                        {background && (
                           <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 1 }}>
                              <img src={backgrounds[background]} style={{ width: "100%" }} />
                           </Box>
                        )}
                     </DownloaderWidget>
                  </Stack>
               </Stack>
            </Stack>
            <Box
               sx={{ height: 1080 * iframeScale }}
               key='downloaderPreview'
            >
               <Box
                  sx={{
                     position: "relative",
                     width: 1920,
                     height: 1080,
                     transform: "scale(" + iframeScale + ")",
                     transformOrigin: "top left",
                     overflow: "hidden",
                     outline: "6px solid #ccc",
                     mt: 3,
                     backgroundColor: "transparent",
                  }}
               >
                  <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 100 }}>
                  <DownloaderPreview
                     activePreview={activePreview}
                     currentPage={currentPage}
                     onPreviewDataUpdate={handlePreviewDataUpdate}
                     sideboardVisible={sideboardVisible}
                  />
                  </Box>

                  {background && (
                     <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 1 }}>
                        <img src={backgrounds[background]} style={{ width: "100%" }} />
                     </Box>
                  )}
               </Box>
            </Box>
         </Grid>
      </Wrapper>
   );
}
